import { AppShell } from '@/components/layout/AppShell';
import { useEffect, useState } from 'react';
import { api, parseErr } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Sparkles, Key, Server, BarChart3, Hash } from 'lucide-react';

export default function AdminSettingsPage() {
  const [ai, setAi] = useState({ provider: 'gemini', model: 'gemini-2.5-flash', has_key: false });
  const [newKey, setNewKey] = useState('');
  const [usage, setUsage] = useState({ total_tokens: 0, by_feature: [] });
  const [codeFmt, setCodeFmt] = useState('32DEC-{YYYY}-{NNN}');

  const load = async () => {
    try {
      const [a, u, c] = await Promise.all([
        api.get('/admin/settings/ai_provider'),
        api.get('/admin/settings/ai_usage'),
        api.get('/admin/settings/code_format'),
      ]);
      setAi(a.data); setUsage(u.data); setCodeFmt(c.data.format);
    } catch (e) { toast.error(parseErr(e)); }
  };
  useEffect(() => { load(); }, []);

  const saveAi = async () => {
    try {
      await api.put('/admin/settings/ai_provider', { provider: ai.provider, model: ai.model, api_key: newKey || undefined });
      setNewKey(''); toast.success('AI provider saved'); load();
    } catch (e) { toast.error(parseErr(e)); }
  };
  const clearKey = async () => {
    try { await api.put('/admin/settings/ai_provider', { provider: ai.provider, model: ai.model, clear_key: true });
      toast.success('Key cleared — falling back to platform key'); load(); } catch (e) { toast.error(parseErr(e)); }
  };
  const saveFmt = async () => {
    try { await api.put('/admin/settings/code_format', { format: codeFmt }); toast.success('Code format saved'); }
    catch (e) { toast.error(parseErr(e)); }
  };

  return (
    <AppShell breadcrumbs={[{ label: 'Admin', to: '/admin/users' }, { label: 'Settings' }]}>
      <div className="p-6 max-w-[900px] mx-auto space-y-6 fade-up" data-testid="admin-settings-page">
        <div>
          <h1 className="font-heading text-3xl font-semibold">Admin · Settings</h1>
          <p className="text-[hsl(var(--foreground-muted))] text-[14px] mt-1">AI provider (bring-your-own key), project code format, and monthly AI token usage.</p>
        </div>

        <section className="card p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-md flex items-center justify-center" style={{ background: 'hsl(var(--accent))', color: 'hsl(var(--accent-foreground))' }}><Key size={17} /></div>
            <div>
              <div className="font-heading font-semibold">AI provider</div>
              <div className="text-[12.5px] text-[hsl(var(--foreground-muted))]">Configure your own Gemini key. All AI features route through it. Unset = platform fallback.</div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <label className="block"><span className="text-[12px] text-[hsl(var(--foreground-muted))]">Provider</span>
              <select className="mt-1 w-full h-9 border rounded-md px-2 text-[14px]" style={{ borderColor: 'hsl(var(--border))' }}
                      value={ai.provider} onChange={e => setAi({ ...ai, provider: e.target.value })} data-testid="admin-ai-provider">
                <option value="gemini">Google Gemini</option>
              </select>
            </label>
            <label className="block"><span className="text-[12px] text-[hsl(var(--foreground-muted))]">Model</span>
              <Input value={ai.model || ''} onChange={e => setAi({ ...ai, model: e.target.value })} placeholder="gemini-2.5-flash" data-testid="admin-ai-model" />
            </label>
            <label className="block"><span className="text-[12px] text-[hsl(var(--foreground-muted))]">API key (leave blank to keep current)</span>
              <Input type="password" value={newKey} onChange={e => setNewKey(e.target.value)} placeholder={ai.has_key ? '••••• (configured)' : 'AIza…'} data-testid="admin-ai-key" />
            </label>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button size="sm" onClick={saveAi} data-testid="admin-ai-save">Save</Button>
            {ai.has_key && <Button size="sm" variant="outline" onClick={clearKey} data-testid="admin-ai-clear">Clear key (use platform)</Button>}
            <span className={`chip ${ai.has_key ? 'chip-success' : 'chip-warning'}`}>{ai.has_key ? 'BYO key active' : 'Platform fallback'}</span>
          </div>
        </section>

        <section className="card p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-md flex items-center justify-center" style={{ background: 'hsl(var(--surface-2))' }}><BarChart3 size={17} /></div>
            <div>
              <div className="font-heading font-semibold">Estimated AI usage this month</div>
              <div className="text-[12.5px] text-[hsl(var(--foreground-muted))]">{usage.month || '—'} · approximate tokens (input+output).</div>
            </div>
          </div>
          <div className="font-heading text-3xl tabular-nums">{(usage.total_tokens || 0).toLocaleString()}<span className="text-[13px] text-[hsl(var(--foreground-muted))] ml-2 font-sans">tokens</span></div>
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
            {(usage.by_feature || []).map(f => (
              <div key={f.feature} className="flex items-center justify-between border rounded-md px-3 py-2 text-[13px]" style={{ borderColor: 'hsl(var(--border))' }}>
                <span>{f.feature}</span>
                <span className="font-mono tabular-nums text-[hsl(var(--foreground-muted))]">{f.tokens.toLocaleString()} · {f.calls}×</span>
              </div>
            ))}
            {(!usage.by_feature || usage.by_feature.length === 0) && <div className="text-[13px] text-[hsl(var(--foreground-muted))]">No AI usage yet this month.</div>}
          </div>
        </section>

        <section className="card p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-md flex items-center justify-center" style={{ background: 'hsl(var(--surface-2))' }}><Hash size={17} /></div>
            <div>
              <div className="font-heading font-semibold">Internal project code format</div>
              <div className="text-[12.5px] text-[hsl(var(--foreground-muted))]">Tokens: {'{YYYY}'} year · {'{YY}'} short year · {'{NNN}'} 3-digit sequential · {'{NNNN}'} 4-digit · {'{N}'} raw. Immutable after activation.</div>
            </div>
          </div>
          <div className="flex gap-2">
            <Input value={codeFmt} onChange={e => setCodeFmt(e.target.value)} data-testid="admin-code-format" />
            <Button onClick={saveFmt} data-testid="admin-code-format-save">Save</Button>
          </div>
        </section>
      </div>
    </AppShell>
  );
}
